import { useEffect, useState } from "react";
import { useAuth } from "../../hooks/GlobalProvider";
import {
  FETCH_Users,
  SettingsCRUD,
  Pannel_Payments,
} from "../../hooks/APIHooks";
import LogoLoader from "../../components/LogoLoader/LogoLoader";

function PannelAccess({ children }) {
  const { user } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transactionId, setTransactionId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [upiId, setUpiId] = useState(""); // fallback UPI ID
  const [transactionSubmitted, setTransactionSubmitted] = useState(false); // New state variable
  const [amount, setAmount] = useState("300.00"); // Default amount

  // Fetch UPI ID from settings API
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(SettingsCRUD);
        const data = await response.json();
        if (data.upi_id) {
          setUpiId(data.upi_id);
        }
      } catch (error) {
        console.error("Error fetching UPI ID from settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const submitTransaction = async (e) => {
    e.preventDefault();
    if (transactionId.length < 10 || transactionId.length > 12) {
      alert("Transaction ID must be 10–12 characters.");
      return;
    }

    if (!/^[A-Za-z0-9]+$/.test(transactionId)) {
      alert("Transaction ID must contain only letters or numbers.");
      return;
    }

    setSubmitting(true);
    setMessage("");

    try {
      const response = await fetch(Pannel_Payments, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user?.user_id,
          phonenumber: user?.phonenumber,
          transaction_id: transactionId.trim(),
          amount: parseFloat(amount),
          notes: "pannel_payment",
        }),
      });

      const data = await response.json();
      setMessage(data.message);
      if (
        data.status === "error" &&
        data.message.includes("already submitted")
      ) {
        setTransactionSubmitted(true);
      }
    } catch (err) {
      console.error("Error submitting transaction:", err);
      setMessage("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        const response = await fetch(FETCH_Users, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });

        const data = await response.json();
        const currentUser = data.users.find(
          (u) => u.phonenumber === user.phonenumber
        );
        setUserData(currentUser);
      } catch (error) {
        console.error("Error fetching user data", error);
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchUserData();
    else setLoading(false);
  }, [user]);

  if (loading) return <LogoLoader />;

  if (!userData || userData.pannel_access === "False") {
    return (
      <div className="min-h-screen z-[100] bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white p-6 rounded-xl shadow-md max-w-sm w-full text-center space-y-4">
          <h1 className="text-xl font-semibold mb-2 text-red-600">
            Access Denied
          </h1>
          <p className="text-gray-600 mb-4">
            Please wait. Your panel access is restricted.
          </p>

          {/* QR Code with dynamic UPI ID */}
          <div className="flex justify-center mb-4">
            {upiId ? (
              <img
                src={upiId}
                alt="UPI QR"
                className="w-40 h-40 object-contain rounded border"
              />
            ) : (
              <p className="text-red-600">UPI QR not available</p>
            )}
          </div>

          {/* Transaction ID Input Form */}
          <form onSubmit={submitTransaction} className="space-y-3">
            <lable>Amount</lable>
            <input
              type="number"
              step="0.01"
              placeholder="Enter Amount"
              value={amount}
              readOnly
              className="border rounded p-2 w-full text-sm bg-gray-100 cursor-not-allowed"
            />

            <lable className="pt-2">Transaction ID</lable>

            <input
              type="text"
              placeholder="Enter Transaction ID"
              value={transactionId}
              onChange={(e) => {
                const value = e.target.value.toUpperCase(); // Optional → convert to uppercase
                if (/^[A-Za-z0-9]*$/.test(value) && value.length <= 12) {
                  setTransactionId(value);
                }
              }}
              minLength={10}
              maxLength={12}
              className="border rounded p-2 w-full text-sm"
              required
              disabled={transactionSubmitted}
            />

            <button
              type="submit"
              disabled={submitting || transactionSubmitted} // Disable button if submitted
              className="w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600 disabled:opacity-50"
            >
              {submitting
                ? "Submitting..."
                : transactionSubmitted
                ? "Submitted"
                : "Submit Payment ID"}
            </button>
          </form>

          {message && (
            <p className="mt-2 text-sm text-gray-600 break-words">{message}</p>
          )}
        </div>
      </div>
    );
  }

  return children;
}

export default PannelAccess;
